/**
 * 未来必付日历契约 —— **F 域 F2**（F 域 PRD §4.6 · `AC-F2.1` ~ `AC-F2.11`）
 *
 * ## 🔴 本文件只定**形状**，不定算法
 * 聚合算法在 `nucleus/upcoming.ts` 的 `composeUpcoming()` —— 那是 **F2 / H 域提醒 / J3 可花** 三方
 * 共用的**唯一实现**（F 域 PRD §4.6「禁止两处各算」· `AC-F2.3`）。
 * 本文件负责把它的输出**加上下钻信息与展示状态**（服务端算好，端侧零日期算术）。
 *
 * ## 三个服务端算好下发的字段（端侧禁自算）
 *
 * | 字段 | 为什么必须服务端算 |
 * |---|---|
 * | `daysUntil` | "今天"要按**账本时区**取（端侧时区可能不同，且小程序与 Web 不同源） |
 * | `status` | 由 `daysUntil` 派生（`overdue` / `today` / `upcoming`）—— 文案与配色都依赖它，散在端侧就会两处不一致 |
 * | `baseCents` | 折算口径唯一在服务端（缺汇率 ⇒ `null`，**不得按 1:1 冒充**，`DR-004`） |
 */

import type { Cents } from '../types/brand.js';
import type { UpcomingKind } from '../nucleus/upcoming.js';

/**
 * 必付项的展示状态（🔴 三态穷举 —— 端侧按它选色与选词，不得自行判断）。
 *
 * ⚠️ 颜色语义遵铁律 8：**逾期是"预警"不是"负债状态"** ⇒ 用 `--c-notice` 琥珀或
 * `--c-alert` 朱红，**不用支出绿**（绿 = 流出方向）。
 */
export const UpcomingStatus = {
  /** 已逾期（`dueDate < 今天`）—— 🔴 未付的过往期次**必须留在日历里**（不静默丢弃） */
  Overdue: 'overdue',
  /** 今天到期 */
  Today: 'today',
  /** 未来 */
  Upcoming: 'upcoming',
} as const;
export type UpcomingStatus = (typeof UpcomingStatus)[keyof typeof UpcomingStatus];

/**
 * 日历上的一项。
 *
 * 🔴 **`id` + `kind` 决定下钻目标**（`AC-F2.8`），`ruleId` 只在 `kind = schedule` 时非空：
 * 日历本身**只读**（`AC-F2.10`：不在日历上改规则）⇒ 下钻是唯一的交互。
 */
export interface UpcomingItemView {
  /** 源 id（周期规则 id / 信用卡账户 id / 贷款期次 id） */
  id: string;
  kind: UpcomingKind;
  /** 展示名（卡名 / 费用名 / 规则名 —— 服务端拼好） */
  label: string;
  /** 到期日（账本本地日历日） */
  dueDate: string;
  /** 距今天数：`0` = 今天；**负 = 已逾期 N 天** */
  daysUntil: number;
  status: UpcomingStatus;
  /** **原币**整数分（不折算、不跨币种相加 —— `DR-004`） */
  originalCents: Cents;
  currency: string;
  /** 折算后的本币分；**缺汇率 = `null`**（不计入合计，也不按 1:1 冒充） */
  baseCents: Cents | null;
  /** 归属账户（下钻用） */
  accountId: string | null;
  accountName: string | null;
  /** 周期规则 id（`kind = schedule` 才有） */
  ruleId: string | null;
  /**
   * 🆕 期次细分（批 80 · `AC-F2.2` 第三数据源）：`'loan'` = **贷款计划表期次**
   * （端侧类型标签显示「贷款」—— `AC-F2.6` 三类标签：周期账 / 信用卡 / 贷款）。
   * 🔴 **可选加性字段**（老消费方不读它行为不变）；`kind` 恒为 `'schedule'`
   * （J3 的「必付未付 ③ 含 loan」按 `schedule` 桶计数 —— 构造性成立，不新增第四桶）。
   */
  subKind?: 'loan';
}

/** `GET /api/upcoming` 的响应（F2 一屏所需全部数据，**单接口下发**） */
export interface UpcomingResponse {
  baseCurrency: string;
  /** 窗口起（= 账本今天）—— 含**已逾期未付**项，故窗口是 `[今天, 今天+30天]` 之外还带过往未付 */
  windowStart: string;
  /** 窗口末（含） */
  windowEnd: string;
  /** 本币合计（**只累加可折算的部分**；`fxIncomplete` 为真时 UI 必须标注"不完整"） */
  totalCents: Cents;
  /** 三构成各自合计（UI 展开明细用） */
  byKind: Readonly<Record<UpcomingKind, Cents>>;
  /** 存在缺汇率的项 ⇒ 合计不完整 */
  fxIncomplete: boolean;
  /** 按到期日升序；同日按三构成固定序（信用卡 → 待缴 → 期次） */
  items: readonly UpcomingItemView[];
}
